import { Minus, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { acceptTypedAge, isCompleteAge, sanitizeAge, stepAge } from './filterState';

const stepBtn =
  'grid h-10 w-10 shrink-0 place-items-center rounded-full border border-soft-gray text-gray-700 hover:border-primary hover:text-primary disabled:opacity-40';

/**
 * A child's age, 0 to 99 in half years: half-year steps with the buttons, or typed.
 * Only digits and ".5" can be typed, and the filter changes only once the value is
 * complete, so "1." on the way to "1.5" is never applied.
 */
export function AgeInput({
  value,
  onChange,
  autoFocus,
}: {
  value: string;
  onChange: (age: string) => void;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation('activities');
  const [text, setText] = useState(value);

  // Follow changes made elsewhere (the buttons, a removed chip, the URL).
  useEffect(() => {
    setText((current) => (sanitizeAge(current) === value ? current : value));
  }, [value]);

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        className={stepBtn}
        aria-label={t('filters.ageDown')}
        disabled={value === '0'}
        onClick={() => onChange(stepAge(value, -0.5))}
      >
        <Minus className="h-4 w-4" aria-hidden="true" />
      </button>
      <input
        type="text"
        inputMode="decimal"
        maxLength={4}
        autoComplete="off"
        autoFocus={autoFocus}
        aria-label={t('filters.childAge')}
        placeholder={t('filters.agePlaceholder')}
        value={text}
        onChange={(e) => {
          const next = acceptTypedAge(e.target.value);
          if (next === null) return;
          setText(next);
          if (isCompleteAge(next)) onChange(sanitizeAge(next));
        }}
        onBlur={() => {
          const clean = sanitizeAge(text);
          setText(clean);
          if (clean !== value) onChange(clean);
        }}
        className="min-w-0 flex-1 rounded-lg border border-soft-gray px-3 py-2 text-center text-base outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
      />
      <button
        type="button"
        className={stepBtn}
        aria-label={t('filters.ageUp')}
        disabled={value === '99'}
        onClick={() => onChange(stepAge(value, 0.5))}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
